/**
 * CSV-Import für Prüfungstermine: Popup zum Einfügen einer CSV (Spalten
 * Fach,Thema,Datum,Form) plus ein fertiger Prompt-Text zum Kopieren nach
 * ChatGPT, der aus einer Sprachnotiz/einem Foto/einer PDF genau so eine CSV
 * erzeugt. Bewusst ohne Deep-Link/Automatik – der Nutzer kopiert Prompt und
 * Antwort selbst hin und her.
 */
import { h, mount } from "@/lib/dom";
import { button, field, textArea, openModal, toast } from "@/components/ui";
import { topicsService, pbErrorMessage } from "@/services";
import { EXAM_TYPE_OPTIONS } from "@/features/topics/examFields";
import { toLocalISODate } from "@/features/timetable/schedule";
import type { ExamType, Subject, Topic } from "@/types/models";

const CSV_COLUMNS = ["Fach", "Thema", "Datum", "Form"] as const;

interface ParsedRow {
  line: number;
  fach: string;
  thema: string;
  datum: string;
  form: string;
  error?: string;
  subject?: Subject;
  existingTopic?: Topic;
  examType: ExamType;
}

/** Ein Feld pro Komma, respektiert `"..."`-Quotierung (Komma/Zeilenumbruch im Feld). */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else cur += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { out.push(cur); cur = ""; }
    else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function matchExamType(label: string): ExamType {
  const norm = label.trim().toLowerCase();
  if (!norm) return "sonstiges";
  const byValue = EXAM_TYPE_OPTIONS.find((o) => o.value === norm);
  if (byValue) return byValue.value;
  const byLabel = EXAM_TYPE_OPTIONS.find((o) => o.label.toLowerCase() === norm);
  return byLabel?.value ?? "sonstiges";
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseExamCsv(text: string, subjects: Subject[], topics: Topic[]): ParsedRow[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  // Kopfzeile erkennen und überspringen, falls vorhanden.
  const first = parseCsvLine(lines[0]).map((s) => s.toLowerCase());
  const startAt = first[0] === "fach" ? 1 : 0;

  return lines.slice(startAt).map((raw, idx) => {
    const [fach = "", thema = "", datum = "", form = ""] = parseCsvLine(raw);
    const row: ParsedRow = { line: startAt + idx + 1, fach, thema, datum, form, examType: matchExamType(form) };

    const subject = subjects.find((s) => s.title.toLowerCase() === fach.toLowerCase());
    if (!subject) { row.error = `Unbekanntes Fach „${fach}"`; return row; }
    row.subject = subject;

    if (!DATE_RE.test(datum)) { row.error = `Ungültiges Datum „${datum}" (Format JJJJ-MM-TT)`; return row; }

    if (thema) {
      // Nur nicht zugewiesene Themen (noch kein Prüfungstermin) sind
      // Kandidaten zum Wiederverwenden – sonst würde ein bereits verplantes
      // Thema versehentlich überschrieben.
      row.existingTopic = topics.find(
        (t) => t.subject === subject.id && !t.examDate && t.state !== "archived" && t.title.toLowerCase() === thema.toLowerCase()
      );
    }
    return row;
  });
}

function csvPromptText(subjects: Subject[], topics: Topic[]): string {
  const openTopics = topics.filter((t) => !t.examDate && t.state !== "archived");
  const subjectList = subjects.length
    ? subjects
        .map((s) => {
          const own = openTopics.filter((t) => t.subject === s.id).map((t) => t.title);
          return `- ${s.title}${own.length ? ` (bestehende offene Themen, wenn passend WIEDERVERWENDEN: ${own.join(", ")})` : ""}`;
        })
        .join("\n")
    : "- (noch keine Fächer angelegt)";
  return `Ich gebe dir gleich Prüfungstermine als Text/Foto/PDF. Erzeuge daraus eine CSV-Tabelle mit GENAU diesen Spalten (Kopfzeile mitschreiben):

Fach,Thema,Datum,Form

Regeln:
- "Fach" muss EXAKT einer dieser vorhandenen Fach-Titel sein (keine Erfindungen, keine Abkürzungen):
${subjectList}
- "Thema": Passt die Prüfung zu einem der oben genannten bestehenden offenen Themen dieses Fachs, dessen Titel EXAKT übernehmen (wird wiederverwendet statt ein neues Thema anzulegen). Sonst ein kurzer neuer Titel (z. B. "Analysis Klausur") – dafür wird ein neues Thema angelegt.
- "Datum" im Format JJJJ-MM-TT (z. B. ${toLocalISODate(new Date())}). Rechne relative Angaben ("nächsten Montag") selbst in ein echtes Datum um, heute ist ${toLocalISODate(new Date())}.
- "Form" ist eine von: ${EXAM_TYPE_OPTIONS.map((o) => o.label).join(", ")}.
- Enthält ein Feld ein Komma, in Anführungszeichen setzen.
- Gib NUR die CSV als Code-Block aus, keinen weiteren Text.`;
}

export function openCsvImportDialog(opts: { subjects: Subject[]; topics: Topic[]; onImported: () => void }): void {
  const { subjects, topics } = opts;

  const promptBox = textArea({ value: csvPromptText(subjects, topics), rows: 10 });
  promptBox.readOnly = true;
  const copyBtn = button({
    label: "Prompt kopieren",
    onClick: () => {
      void navigator.clipboard.writeText(promptBox.value).then(
        () => toast("In die Zwischenablage kopiert.", "success"),
        () => toast("Kopieren fehlgeschlagen – bitte manuell markieren.", "error")
      );
    },
  });

  const csvBox = textArea({ placeholder: `${CSV_COLUMNS.join(",")}\nMathematik,Analysis Klausur,2026-09-10,Klausur`, rows: 8 });
  const preview = h("div", { class: "csv-import-preview" });
  const importBtn = button({ label: "Importieren", variant: "primary", disabled: true, onClick: () => void doImport() });
  let rows: ParsedRow[] = [];

  function renderPreview() {
    rows = parseExamCsv(csvBox.value, subjects, topics);
    const valid = rows.filter((r) => !r.error);
    importBtn.disabled = valid.length === 0;
    mount(
      preview,
      rows.length === 0
        ? h("p", { class: "muted text-sm" }, "Noch keine Zeilen erkannt.")
        : h(
            "ul",
            { class: "csv-import-rows" },
            ...rows.map((r) =>
              h(
                "li",
                { class: r.error ? "csv-row-error" : "csv-row-ok" },
                r.error
                  ? `Zeile ${r.line}: ${r.error}`
                  : `Zeile ${r.line}: ${r.subject!.title} · ${r.thema || "Prüfung"} · ${r.datum} · ${r.existingTopic ? "aktualisiert" : "neues Thema"}`
              )
            )
          )
    );
  }
  csvBox.addEventListener("input", renderPreview);
  renderPreview();

  async function doImport() {
    const valid = rows.filter((r) => !r.error);
    importBtn.disabled = true;
    let ok = 0;
    let failed = 0;
    for (const row of valid) {
      try {
        if (row.existingTopic) {
          await topicsService.setExam(row.existingTopic.id, { date: row.datum, examType: row.examType });
        } else {
          await topicsService.create({
            subject: row.subject!.id,
            title: row.thema || "Prüfung",
            state: "active",
            examDate: row.datum,
            examType: row.examType,
          });
        }
        ok++;
      } catch (err) {
        failed++;
        toast(`Zeile ${row.line}: ${pbErrorMessage(err)}`, "error");
      }
    }
    toast(`${ok} Termin${ok === 1 ? "" : "e"} importiert${failed ? `, ${failed} fehlgeschlagen` : ""}.`, failed ? "warning" : "success");
    opts.onImported();
    if (failed === 0) handle.close();
    else importBtn.disabled = false;
  }

  const handle = openModal({
    title: "CSV-Import Prüfungstermine",
    wide: true,
    guardUnsaved: false,
    body: h(
      "div",
      { class: "col" },
      field("1. Prompt für ChatGPT (o. Ä.) kopieren", h("div", { class: "col" }, promptBox, copyBtn)),
      field("2. CSV-Antwort einfügen", csvBox),
      preview
    ),
    footer: h("div", { class: "row" }, button({ label: "Schließen", variant: "ghost", onClick: () => handle.close() }), importBtn),
  });
}
